import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import "firebase/compat/storage";
import "firebase/compat/auth";

import firebaseConfig from "../firebasecfg";

if (!firebase.apps.length) {
  firebase.initializeApp(firebaseConfig);
}

const db = firebase.firestore();
const storage = firebase.storage();

const getImageRef = (collectionId, itemId) =>
  storage.ref(`/${collectionId}/${itemId}/image`);

export const getMoodboardImageRef = (moodboardId, itemId) =>
  storage.ref(`/moodboards/${moodboardId}/${itemId}/image`);

export const subscribeToCollection = (
  collectionName,
  admin,
  userId,
  callback
) => {
  let query = db.collection(collectionName);

  // If not admin and no userId, only fetch public moodboards
  // If userId exists, fetch all moodboards and filter client-side
  if (!admin && !userId) {
    query = query.where("public", "==", true);
  }
  // If admin or userId exists, fetch all moodboards (filtering happens client-side)

  let unsubscribe = query.onSnapshot(
    (snap) => {
      let items = {};
      let meta = {};
      for (let i in snap.docs) {
        let doc = snap.docs[i];
        if (doc.id === "meta") {
          meta = doc.data();
        } else {
          let itemData = doc.data();
          // Filter: show if admin, public, or created by current user
          if (
            admin ||
            itemData.public ||
            (userId && itemData.createdBy === userId)
          ) {
            items[doc.id] = itemData;
          }
        }
      }
      callback({ items, meta });
    },
    (err) => {
      callback({ error: "error getting  items" });
    }
  );
  callback({ unsubscribe });
};

const deleteItemImage = (collectionId, itemId, callback) => {
  let imageRef = getImageRef(collectionId, itemId);
  imageRef
    .delete()
    .then(() => {
      callback({ success: true });
    })
    .catch((err) => {
      if (err.code === "storage/object-not-found") {
        callback({ success: true });
      } else {
        callback({ error: "Error deleting image from storage" });
      }
    });
};

const dbDeleteItem = (collectionId, itemId, callback) => {
  db.collection(collectionId)
    .doc(itemId)
    .delete()
    .then(() => {
      callback({ success: true });
    })
    .catch((err) => {
      callback({ error: "Error deleting  item" });
    });
};

export const deleteCollectionItem = (collectionId, itemId, callback) => {
  deleteItemImage(collectionId, itemId, ({ error }) => {
    if (error) {
      callback({ error });
    } else {
      dbDeleteItem(collectionId, itemId, callback);
    }
  });
};

export const deleteDocumentAndSubcollection = (
  rootCollectionId,
  documentId,
  collectionId,
  callback
) => {
  db.collection(rootCollectionId)
    .doc(documentId)
    .collection(collectionId)
    .get()
    .then((res) => {
      if (res.docs.length) {
        let dbPromises = res.docs.map((doc) => doc.ref.delete());
        Promise.all(dbPromises)
          .then(() => {
            let ids = res.docs.map((doc) => doc.id);
            let promises = ids.map((id) =>
              getMoodboardImageRef(documentId, id).delete()
            );
            Promise.allSettled(promises).then((results) => {
              db.collection(rootCollectionId)
                .doc(documentId)
                .delete()
                .then(() => {
                  callback({ success: true });
                })
                .catch((err) => {
                  callback({
                    error: "error deleting moodboard",
                  });
                });
            });
          })
          .catch((err) => {
            callback({
              error: "Error deleting collection from db",
            });
          });
      } else {
        db.collection(rootCollectionId)
          .doc(documentId)
          .delete()
          .then(() => {
            callback({ success: true });
          })
          .catch((err) => {
            callback({ error: "error deleting moodboard" });
          });
      }
    })
    .catch((err) => {
      callback({ error: "Error deleting collection" });
    });
  //DELETE FILES IF NECESSARY
};

const uploadImage = (collectionId, itemId, blob, callback) => {
  if (itemId) {
    if (blob) {
      let imageRef = getImageRef(collectionId, itemId);
      let uploadTask = imageRef.put(blob);

      uploadTask
        .then(({ ref }) => {
          ref
            .getDownloadURL()
            .then((url) => callback({ uploadState: "success", url }))
            .catch({
              uploadError: "error",
              error: "error getting image url",
            });
        })
        .catch((err) => {
          callback({
            uploadState: "error",
            error: "error uploading image",
          });
        });

      callback({ cancel: uploadTask.cancel });

      uploadTask.on(
        "state_changed",
        ({ state, bytesTransferred, totalBytes }) => {
          switch (state) {
            case firebase.storage.TaskState.RUNNING:
              let progress = bytesTransferred / totalBytes;
              callback({ uploadState: "progress", progress });
              break;
            case firebase.storage.TaskState.ERROR:
              callback({
                uploadState: "error",
                error: "Error during upload",
              });
              break;
            case firebase.storage.TaskState.CANCELED:
              callback({ uploadState: "cancelled" });
              break;
            default:
              break;
          }
        }
      );
    } else {
      callback({
        error: "ERROR: no blob provided when uploading item image",
      });
    }
  } else {
    callback({ error: "ERROR: no item id when uploading item image" });
  }
};

const dbSetItem = (collectionId, itemId, item, callback) => {
  if (itemId) {
    delete item.progress;
    delete item.error;
    delete item.uploading;
    delete item.dataUrl;
    delete item.blob;

    db.collection(collectionId)
      .doc(itemId)
      .set(item, { merge: true })
      .then(() => {
        if (callback) callback({ success: true });
      })
      .catch((err) => {
        if (callback)
          callback({
            error: "Error editing  item in database",
          });
      });
  } else {
    if (callback) callback({ error: "Error updating  item - no item id" });
  }
};

export const saveCollectionItem = (collectionId, itemId, item, callback) => {
  if (itemId) {
    if (item.blob) {
      uploadImage(
        collectionId,
        itemId,
        item.blob,
        ({ uploadState, error, progress, url }) => {
          switch (uploadState) {
            case "error":
              callback({ error });
              break;
            case "success":
              delete item.dataUrl;
              delete item.blob;
              item.url = url;
              dbSetItem(collectionId, itemId, item, callback);
              break;
            case "cancelled":
              callback({ error: "cancelled" });
              break;
            case "progress":
              callback({ progress });
              break;
            default:
              break;
          }
        }
      );
    } else {
      dbSetItem(collectionId, itemId, item, callback);
    }
  } else {
    callback({ error: "Error updating  item - no item id" });
  }
};

export const createCollectionItem = (collectionId, data, callback) => {
  let newId = db.collection(collectionId).doc().id;

  // Add createdBy field with current user's uid if available
  const currentUser = firebase.auth().currentUser;
  if (currentUser && currentUser.uid) {
    data.createdBy = currentUser.uid;
  }

  saveCollectionItem(
    collectionId,
    newId,
    data,
    ({ error, success, progress }) => {
      if (progress) {
        callback({ progress });
      }
      if (success) {
        callback({ id: newId });
      } else {
        callback({ error });
      }
    }
  );
};

export const saveCollectionMeta = (collectionId, data, callback) => {
  if (collectionId) {
    db.collection(collectionId)
      .doc("meta")
      .set(data)
      .then(() => {
        callback({ success: true });
      })
      .catch((err) => {
        callback({
          error: "Error setting meta data for collection " + collectionId,
        });
      });
  } else {
    callback({ error: "Error saving collection meta" });
  }
};
